import { useTranslation } from "react-i18next";
import { cn, Icon } from "@nexus/ui";
import type { LlmUsageStats } from "@/features/llm-usage/api/llm-usage.api";
import { AdminKpiStrip, type AdminKpiMetric } from "@/features/admin/components/layout";
import { formatDuration } from "@/features/admin/lib/format";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import { costDisplay } from "../lib/cost-display";
import { formatTokensCompact, formatTokensExact } from "../lib/format-tokens";

interface LlmUsageKpiStripProps {
  stats: LlmUsageStats | undefined;
  isLoading: boolean;
  /** These numbers still belong to the previous window; the new one is in flight. */
  isStale: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /**
   * The p95 an alert fires at, or null when nothing should be marked.
   * `alertThresholdForWindow` decides that — a level only means something when
   * the alert is on and evaluates this same window.
   */
  latencyAlertP95Ms?: number | null;
}

/**
 * The four numbers that answer "how much did we burn, and how hard was it",
 * with live latency pinned apart from them.
 *
 * Totals are abbreviated because they run to millions; the exact figure rides
 * on each metric's title rather than in the layout.
 */
export function LlmUsageKpiStrip({
  stats,
  isLoading,
  isStale,
  isError = false,
  onRetry,
  latencyAlertP95Ms = null,
}: LlmUsageKpiStripProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const cost = stats ? costDisplay(stats, locale) : null;
  const isP95OverAlert =
    latencyAlertP95Ms != null &&
    stats?.p95LatencyMs != null &&
    stats.p95LatencyMs >= latencyAlertP95Ms;

  const metrics: AdminKpiMetric[] = stats
    ? [
        {
          id: "totalTokens",
          label: t("admin.llmUsage.kpi.totalTokens", "Tokens"),
          value: formatTokensCompact(stats.totalTokens, locale),
          isMuted: stats.totalTokens === 0,
          title: formatTokensExact(stats.totalTokens, locale),
          // Reported separately from output rather than folded into it: it is
          // billed, it is invisible in the response, and it is the number that
          // explains a bill nobody can account for from the text they got back.
          sub:
            stats.thinkingTokens > 0
              ? t("admin.llmUsage.kpi.thinking", "{{tokens}} thinking", {
                  tokens: formatTokensCompact(stats.thinkingTokens, locale),
                })
              : undefined,
        },
        {
          // Input and output are one split, so they read as one metric rather
          // than two identical ones.
          id: "inOut",
          label: t("admin.llmUsage.kpi.inOut", "In / out"),
          value: `${formatTokensCompact(stats.inputTokens, locale)} / ${formatTokensCompact(
            stats.outputTokens,
            locale,
          )}`,
          isMuted: stats.totalTokens === 0,
          title: t("admin.llmUsage.kpi.inOutTitle", "{{input}} in, {{output}} out", {
            input: formatTokensExact(stats.inputTokens, locale),
            output: formatTokensExact(stats.outputTokens, locale),
          }),
          sub:
            stats.cacheReadTokens > 0
              ? t("admin.llmUsage.kpi.cached", "{{tokens}} cached", {
                  tokens: formatTokensCompact(stats.cacheReadTokens, locale),
                })
              : undefined,
        },
        {
          id: "calls",
          label: t("admin.llmUsage.kpi.calls", "Calls"),
          value: stats.calls.toLocaleString(locale),
          isMuted: stats.calls === 0,
        },
        {
          // The one number nobody has to convert before taking it to a meeting.
          // It is only as complete as the rates behind it, so an incomplete
          // figure says so here rather than reading as the whole bill.
          id: "cost",
          label: t("admin.llmUsage.kpi.cost", "Est. cost"),
          value: cost?.text,
          isMuted: cost?.isUnpriced || stats.costMicros === 0,
          title: cost?.isUnpriced
            ? t(
                "admin.llmUsage.kpi.costUnpricedTitle",
                "No prices are set for the models used in this window. Set them in Admin → Settings → Operations.",
              )
            : undefined,
          sub: cost?.isUnpriced
            ? t("admin.llmUsage.kpi.noPrices", "no prices set")
            : cost?.isPartial
              ? t("admin.llmUsage.kpi.costPartial", "{{tokens}} unpriced", {
                  tokens: formatTokensCompact(stats.unpricedTokens, locale),
                })
              : undefined,
        },
      ]
    : [];

  const live: AdminKpiMetric | undefined = stats
    ? {
        id: "avgLatency",
        label: t("admin.llmUsage.kpi.avgLatency", "Avg latency"),
        value: stats.avgLatencyMs == null ? null : formatDuration(stats.avgLatencyMs),
        sub:
          stats.p95LatencyMs == null ? undefined : (
            <span
              className={cn(isP95OverAlert && "flex items-center gap-1 text-warning")}
              title={
                isP95OverAlert
                  ? t(
                      "admin.llmUsage.alerts.latencyOverTitle",
                      "Past the p95 {{duration}} latency alert level for this window",
                      {
                        duration: formatPreciseDurationMs(latencyAlertP95Ms ?? 0),
                      },
                    )
                  : undefined
              }
            >
              {/* Colour alone would leave the number reading as an ordinary
                  secondary figure to anyone who cannot see the warning hue. */}
              {isP95OverAlert ? (
                <Icon
                  name="alert-circle"
                  size="xs"
                  colorVar="--warning"
                  aria-label={t(
                    "admin.llmUsage.alerts.latencyOver",
                    "Past the latency alert level",
                  )}
                />
              ) : null}
              {t("admin.llmUsage.kpi.p95Latency", "p95 {{duration}}", {
                duration: formatPreciseDurationMs(stats.p95LatencyMs),
              })}
            </span>
          ),
      }
    : undefined;

  return (
    <AdminKpiStrip
      metrics={metrics}
      live={live}
      isLoading={isLoading || !stats}
      isStale={isStale}
      isError={isError}
      onRetry={onRetry}
    />
  );
}
